import { OrderSummaryCard } from '@euler/app/components/order/summary/OrderSummaryCard';
import { OrderDetailContent } from '@euler/app/flows/order/components/OrderDetailContent';
import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import { useServiceRecordQrcodeSharing } from '@euler/app/flows/record/functions/useServiceRecordQrcodeSharing';
import { ChildRecordList } from '@euler/app/flows/record/pages/detail/components/ChildRecordList';
import { QuotationSection } from '@euler/app/flows/record/pages/detail/components/QuotationSection';
import { ServiceSubjects } from '@euler/app/flows/record/pages/detail/components/ServiceSubjects';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import { HStack } from '@euler/components/layout/Stack';
import {
  ResponsiveContextProvider,
  useResponsiveContext,
} from '@euler/functions/responsive/ResponsiveContext';
import { PixelScaleFn } from '@euler/functions/responsive/utils';
import { useIsTabletLandscapeLayout } from '@euler/functions/responsiveness';
import { OrderDetail } from '@euler/model';
import { InspectionTaskStatus } from '@euler/model/enum';
import { Maybe } from '@euler/types';
import { memo, useCallback } from 'react';
import { Platform, ScrollView } from 'react-native';
import { ServiceRecordCommentSection } from './CommentSection';
import { FaultInspections } from './FaultInspections';
import { MaintenancePlans } from './MaintenancePlans';
import { ServiceRecordMenu } from './Menu';
import { RecentServiceRecords } from './RecentServiceRecords';
import { ServiceRecordRemarkSection } from './RemarkSection';
import { ReportList } from './ReportList';

export const ServiceRecordDetailContentForIpad = memo(
  ({
    orderDetail,
  }: {
    orderDetail: Maybe<OrderDetail>;
    onShowDetail?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { serviceRecord, refresh } = useServiceRecordContext();

    const { onShare: onQrcodeSharingPress } = useServiceRecordQrcodeSharing(
      () => refresh(true /** refresh silently */),
    );

    const isLandscape = useIsTabletLandscapeLayout();

    const modifiedRpx: PixelScaleFn = useCallback(
      (value, options) => {
        return rpx(value, options) * 0.83;
      },
      [rpx],
    );

    return (
      <HStack
        alignItems="stretch"
        css={`
          flex: 1;
          flex-direction: row-reverse;
        `}
      >
        <ScrollView
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          contentContainerStyle={{
            paddingTop: rpx(15),
            paddingRight: rpx(15),
            paddingBottom: rpx(30),
          }}
        >
          <OrderSummaryCard
            orderDetail={orderDetail}
            isMainOrder={serviceRecord.isMainRecord}
            isFirstTimeOrder={serviceRecord.isFirstTimeOrder ?? false}
            status={InspectionTaskStatus.Finished}
            createdAt={serviceRecord.orderedAt}
            finishedAt={serviceRecord.finishedAt}
            orderNo={serviceRecord.orderNo}
            serviceRecordId={__DEV__ ? serviceRecord._id : undefined}
            licensePlateNo={serviceRecord.licensePlateNo}
            vin={serviceRecord.vin}
            storeName={serviceRecord.storeName}
            serviceAgentName={serviceRecord.serviceAgentName}
            serviceAgentChatByName
            vehicleMileage={serviceRecord.mileage}
            vehicleName={serviceRecord.vehicleName}
            vehicleBrandLogo={serviceRecord.vehicleBrandLogo}
            vehicleFuelType={serviceRecord.vehicleFuelType}
            customerSigned={serviceRecord.signImgUrl != null}
            customerType={serviceRecord.customerType}
            customerName={serviceRecord.customerName}
            customerMobile={serviceRecord.customerMobile}
            returnType={serviceRecord.returnType}
            carwashType={serviceRecord.carwashType}
            oldPartDisposalType={serviceRecord.oldPartDisposalType}
            reservationType={serviceRecord.reservationType}
            estimatedFinishTime={null}
            insuranceExpiryDate={serviceRecord.insuranceExpiryDate}
            tags={serviceRecord.tags}
            subscribed={serviceRecord.subscribed}
            brandLogoPosition="right"
            hideServiceRecordCount
            hideCustomerInfo
            onQrcodeSharingPress={onQrcodeSharingPress}
            css={`
              margin-bottom: ${rpx(15)}px;
            `}
          />
          <ServiceRecordMenu showRollback />
          <ServiceSubjects />
          {serviceRecord.isMainRecord && <MaintenancePlans />}
          <FaultInspections />
          <QuotationSection />
          <ReportList />
          {serviceRecord.hasChildRecords && <ChildRecordList />}
          <ServiceRecordRemarkSection remark={serviceRecord.remark} />
          <ServiceRecordCommentSection comment={serviceRecord.comment} />
          <RecentServiceRecords />
        </ScrollView>
        <TaskDetailSection
          title="订单详情"
          bodyStyle={{
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: rpx(15),
            flex: 1,
          }}
          containerStyle={{
            width: isLandscape ? '45%' : '45%',
            paddingTop: rpx(15),
            paddingLeft: rpx(15),
            paddingRight: rpx(15),
            alignSelf: 'stretch',
          }}
          css={`
            flex: 1;
          `}
        >
          <ScrollView>
            <ResponsiveContextProvider scaled={modifiedRpx}>
              <OrderDetailContent
                hideBasicInfoTitle
                hideVIN
                hideMileage
                hideTags
                hideRemark
                hideServiceAgent
              />
            </ResponsiveContextProvider>
          </ScrollView>
        </TaskDetailSection>
      </HStack>
    );
  },
);
